import { useState, useEffect, useRef, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, isToday, isBefore, startOfToday, getDay, setHours, setMinutes, addDays } from 'date-fns'
import { ChevronLeft, ChevronRight, Clock, Check, Pencil, X, Undo2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useBookingSystem, type Booking } from '@/contexts/BookingSystemContext'
import type { TimeSlot, WeekDay, ArtistAvailability } from '@/types/booking'
import { Timestamp, doc, updateDoc, onSnapshot } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { useAuthState } from '@/hooks/useAuth'
import { useUserData } from '@/hooks/useUserData'
import { useCalendarSync } from '@/hooks/useCalendarSync'
import { CREATE_BOOKING_ENDPOINT, CANCEL_BOOKING_ENDPOINT } from '@/config/endpoints'
import { useNavigate } from 'react-router-dom'
import { ConsentModal } from '@/components/chat/booking/ConsentModal'
import { fetchArtistById } from '@/lib/tattooData'
import { useLongPress } from '@/hooks/useLongPress'
import { deleteMessageFromChat } from '@/lib/chatData'
import { useAnalytics } from '@/hooks/useAnalytics'
import { sendMetaStandardEvent } from '@/lib/metaEvents'
import { isNative } from '@/lib/platform'

interface BookingSelectionMessageProps {
  estimatedTime: number | 'unknown'
  earliestDate: Date | Timestamp
  preparationDays?: number // Number of days from current date before client can book
  senderName?: string
  isOwnMessage: boolean // true if current user sent this message (artist view)
  artistId?: string // The artist who sent this booking message
  chatId: string // The chat ID for this conversation
  onBookingCreated?: () => void // Callback when booking is successfully created
  messageId?: string // Message ID for deletion
  onMessageDeleted?: () => void // Callback when message is deleted
}

// Helper to convert day index to WeekDay
const getDayName = (date: Date): WeekDay => {
  const days: WeekDay[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
  return days[getDay(date)]
}

// Helper to format time from minutes
const formatTime = (minutes: number): string => {
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`
}

/**
 * Subtract blocked time slots from available time slots
 * Example: Available 9am-5pm (540-1020), Blocked 2pm-3pm (840-900)
 * Result: [9am-2pm (540-840), 3pm-5pm (900-1020)]
 */
const subtractBlockedSlots = (available: TimeSlot[], blocked: TimeSlot[]): TimeSlot[] => {
  if (blocked.length === 0) return available
  
  const result: TimeSlot[] = []
  
  available.forEach(availSlot => {
    let currentStart = availSlot.start
    
    // Sort blocked slots by start time
    const sortedBlocked = [...blocked].sort((a, b) => a.start - b.start)
    
    sortedBlocked.forEach(blockedSlot => {
      // If the blocked slot doesn't overlap with remaining availability, skip it
      if (blockedSlot.end <= currentStart || blockedSlot.start >= availSlot.end) {
        return
      }
      
      // Add the gap before the blocked slot (if any)
      if (currentStart < blockedSlot.start) {
        result.push({
          start: currentStart,
          end: blockedSlot.start
        })
      }
      
      // Move current position past the blocked slot
      currentStart = Math.max(currentStart, blockedSlot.end)
    })
    
    // Add any remaining availability after processing blocked slots
    if (currentStart < availSlot.end) {
      result.push({
        start: currentStart,
        end: availSlot.end
      })
    }
  })
  
  return result
}

export function BookingSelectionMessage({
  estimatedTime,
  earliestDate: earliestDateProp,
  preparationDays,
  senderName,
  isOwnMessage,
  artistId,
  chatId,
  onBookingCreated,
  messageId,
  onMessageDeleted
}: BookingSelectionMessageProps) {
  // Calculate the earliest date dynamically based on preparation days
  // This ensures the preparation time starts from the current viewing date, not the sending date
  const earliestDate = useMemo(() => {
    if (preparationDays !== undefined && preparationDays !== null) {
      // Use preparation days to calculate from current date
      return addDays(startOfToday(), preparationDays)
    }
    // Fallback to the fixed earliestDate from message (legacy behavior)
    return earliestDateProp instanceof Timestamp ? earliestDateProp.toDate() : earliestDateProp
  }, [preparationDays, earliestDateProp])

  const { user } = useAuthState()
  const { userData } = useUserData()
  const { trackEvent } = useAnalytics()
  const { 
    availability: ownAvailability, 
    subscribeToBookingByMessageId,
    requiredInformation: ownRequiredInformation
  } = useBookingSystem()
  const { createBookingEvent, deleteBookingEvent, isIosCalendar } = useCalendarSync()
  const navigate = useNavigate()
  const [artistAvailability, setArtistAvailability] = useState<ArtistAvailability | null>(null)
  const [artistRequiredInformation, setArtistRequiredInformation] = useState<any | null>(null)
  const [currentMonth, setCurrentMonth] = useState(earliestDate || new Date())
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<{ start: number; end: number } | null>(null)
  const [slideDirection, setSlideDirection] = useState(0)
  const [bookingStep, setBookingStep] = useState<'calendar' | 'form'>('calendar')
  const [justSelectedDate, setJustSelectedDate] = useState(false)
  
  // Form state - dynamic based on requiredInformation
  const [formData, setFormData] = useState<Record<string, string | boolean>>({})
  
  // API state
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  
  // Booking state
  const [existingBooking, setExistingBooking] = useState<Booking | null>(null)
  const [isCancelling, setIsCancelling] = useState(false)
  
  // Consent modal state
  const [activeConsentField, setActiveConsentField] = useState<string | null>(null)
  
  // Artist display name state
  const [artistDisplayName, setArtistDisplayName] = useState<string | null>(null)
  
  // Delete overlay state
  const [showDeleteOverlay, setShowDeleteOverlay] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [messagePosition, setMessagePosition] = useState({ top: 0, left: 0, width: 0, height: 0 })
  const [messageSnapshot, setMessageSnapshot] = useState<{ html: string; className: string } | null>(null)
  const messageRef = useRef<HTMLDivElement>(null)
  
  // Artist sent the message = they see greyed out view
  // User receives the message = they see interactive view
  const isArtistView = isOwnMessage
  

  // Cancel booking handler
  const handleCancelBooking = async () => {
    if (!existingBooking) return

    console.log('[BookingSelectionMessage:cancelBooking] 🔴 Starting booking cancellation flow')
    console.log('[BookingSelectionMessage:cancelBooking] Booking to cancel:', {
      bookingId: existingBooking.id,
      clientUsername: existingBooking.clientUsername,
      chatId: existingBooking.chatId
    })

    setIsCancelling(true)
    try {
      // Delete iOS calendar event first (if exists) - must be done on-device before backend call
      // Google Calendar deletion is handled by the backend
      if (existingBooking.iosCalendarEventId) {
        console.log('[BookingSelectionMessage:cancelBooking] 📅 Deleting iOS Calendar event')
        await deleteBookingEvent({
          iosCalendarEventId: existingBooking.iosCalendarEventId,
          googleCalendarEventId: existingBooking.googleCalendarEventId
        })
      }

      console.log('[BookingSelectionMessage:cancelBooking] Calling backend to cancel booking')
      const response = await fetch(CANCEL_BOOKING_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          bookingId: existingBooking.id
        })
      })

      console.log('[BookingSelectionMessage:cancelBooking] Backend response:', {
        status: response.status,
        statusText: response.statusText
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        console.error('[BookingSelectionMessage:cancelBooking] ❌ Backend error:', errorData)
        throw new Error(errorData.message || `Failed to cancel booking: ${response.statusText}`)
      }

      console.log('[BookingSelectionMessage:cancelBooking] ✅ Booking cancelled (Google Calendar: backend, iOS Calendar: on-device)')

      // Clear the existing booking to show the initial view again
      console.log('[BookingSelectionMessage:cancelBooking] Clearing UI state')
      setExistingBooking(null)
      setSelectedDate(null)
      setSelectedTimeSlot(null)
      setBookingStep('calendar')
      
      console.log('[BookingSelectionMessage:cancelBooking] ✅ Booking cancellation complete!')
    } catch (err) {
      console.error('[BookingSelectionMessage:cancelBooking] ❌ Error cancelling booking:', err)
      console.error('[BookingSelectionMessage:cancelBooking] Error details:', {
        name: err instanceof Error ? err.name : 'Unknown',
        message: err instanceof Error ? err.message : String(err)
      })
      setError(err instanceof Error ? err.message : 'Failed to cancel booking. Please try again.')
    } finally {
      setIsCancelling(false)
    }
  }

  // Update message position and capture snapshot when overlay is shown
  useEffect(() => {
    if (showDeleteOverlay && messageRef.current) {
      const rect = messageRef.current.getBoundingClientRect()
      setMessagePosition({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height
      })
      // Capture the message HTML and classes for rendering above blur
      setMessageSnapshot({
        html: messageRef.current.innerHTML,
        className: messageRef.current.className
      })
    }
  }, [showDeleteOverlay])

  const handleLongPress = () => {
    // Only allow deletion of own messages that don't have an existing booking
    if (isOwnMessage && messageId && chatId && !existingBooking) {
      setShowDeleteOverlay(true)
    }
  }

  const handleDelete = async () => {
    if (!messageId || !chatId) return
    
    setIsDeleting(true)
    try {
      await deleteMessageFromChat(chatId, messageId)
      if (onMessageDeleted) {
        onMessageDeleted()
      }
      // Message will be removed automatically via Firestore listener
    } catch (error) {
      console.error('Failed to delete message:', error)
      setIsDeleting(false)
      setShowDeleteOverlay(false)
    }
  }

  const handleCancelDelete = () => {
    setShowDeleteOverlay(false)
  }

  // Check if this booking is for May 9th, 2026
  const isTime4TatsBooking = useMemo(() => {
    if (existingBooking?.scheduledTimestamp) {
      const bookingDate = existingBooking.scheduledTimestamp.toDate()
      return bookingDate.getFullYear() === 2026 && 
             bookingDate.getMonth() === 4 && 
             bookingDate.getDate() === 9
    }
    return false
  }, [existingBooking])

  // Long press handlers
  const longPressHandlers = useLongPress({
    onLongPress: handleLongPress,
    ms: 500
  })

  // Clear the highlight after brief delay
  useEffect(() => {
    if (justSelectedDate) {
      const timer = setTimeout(() => {
        setJustSelectedDate(false)
      }, 200)
      return () => clearTimeout(timer)
    }
  }, [justSelectedDate])

  // Check if booking already exists for this chat on initial load
  useEffect(() => {
    if (!messageId || !chatId) {
      return
    }
    
    console.log('[BookingSelectionMessage] Setting up booking subscription for chatId:', chatId, 'messageId:', messageId)
    
    // Subscribe to booking changes for THIS specific message
    const unsubscribe = subscribeToBookingByMessageId(chatId, messageId, (booking) => {
      console.log('[BookingSelectionMessage] Booking update received:', {
        bookingExists: !!booking,
        bookingStatus: booking?.status,
        bookingMessageId: booking?.messageId,
        thisMessageId: messageId,
        isArtistView
      })
      
      // The subscription is already filtered by messageId in Firestore,
      // so any booking we receive here is guaranteed to be for this message
      setExistingBooking(booking)
    })
    
    return () => {
      console.log('[BookingSelectionMessage] Cleaning up booking subscription')
      unsubscribe()
    }
  }, [chatId, subscribeToBookingByMessageId, messageId, isArtistView])

  // Set up real-time listener for artist's availability and display name
  useEffect(() => {
    if (!isArtistView && artistId) {
      // Set up real-time listener for availability
      const bookingsRef = doc(db, 'bookingSystems', artistId)
      const unsubscribe = onSnapshot(
        bookingsRef,
        (bookingsDoc) => {
          try {
            if (bookingsDoc.exists()) {
              const data = bookingsDoc.data()
              setArtistAvailability(data.availability || null)
              
              // Check for required information in both formats
              // Primary: bookingClientInfo (array format - current)
              // Fallback: bookingRequiredInformation (map format - legacy)
              let requiredInfo = null
              if (data.bookingClientInfo && Array.isArray(data.bookingClientInfo)) {
                requiredInfo = data.bookingClientInfo
              } else if (data.bookingRequiredInformation) {
                requiredInfo = data.bookingRequiredInformation
              }
              setArtistRequiredInformation(requiredInfo)
            }
          } catch (error) {
            console.error('Error processing artist availability:', error)
          }
        },
        (error) => {
          console.error('Error listening to artist availability:', error)
        }
      )
      
      // Fetch artist display name (one-time fetch is fine for this)
      const fetchArtistDisplayName = async () => {
        try {
          const artist = await fetchArtistById(artistId)
          if (artist) {
            setArtistDisplayName(artist.displayName || artist.firstName || null)
          }
        } catch (error) {
          console.error('Error fetching artist display name:', error)
        }
      }
      
      fetchArtistDisplayName()
      
      return () => {
        unsubscribe()
      }
    }
  }, [isArtistView, artistId])

  // Use artist's availability for user view, own availability for artist view
  const availability = isArtistView ? ownAvailability : artistAvailability
  const requiredInformation = isArtistView ? ownRequiredInformation : artistRequiredInformation

  const handlePrevMonth = () => {
    setSlideDirection(-1)
    setCurrentMonth(prev => subMonths(prev, 1))
  }

  const handleNextMonth = () => {
    setSlideDirection(1)
    setCurrentMonth(prev => addMonths(prev, 1))
  }

  const handleDayClick = (date: Date) => {
    const isSelecting = !isSameDay(date, selectedDate || new Date('1970-01-01'))
    setSelectedDate(isSelecting ? date : null)
    setSelectedTimeSlot(null) // Reset time slot when changing date
    if (isSelecting) {
      setJustSelectedDate(true) // Trigger the highlight animation
    }
  }

  const handleTimeSlotClick = (slot: { start: number; end: number }) => {
    setSelectedTimeSlot(
      selectedTimeSlot?.start === slot.start && selectedTimeSlot?.end === slot.end 
        ? null 
        : slot
    )
  }

  const handleNextClick = () => {
    if (selectedDate && selectedTimeSlot) {
      setBookingStep('form')
    }
  }

  const handleBackToCalendar = () => {
    setBookingStep('calendar')
  }

  const handleBookTime = async () => {
    if (!selectedDate || !selectedTimeSlot || !user) return

    console.log('[BookingSelectionMessage:createBooking] 🎫 Starting booking creation flow')
    console.log('[BookingSelectionMessage:createBooking] Selection details:', {
      selectedDate: selectedDate.toISOString(),
      selectedTimeSlot: selectedTimeSlot,
      user: user.email,
      artistId: artistId,
      chatId: chatId
    })

    setIsSubmitting(true)
    setError(null)

    try {
      // Create timestamp from selected date and time slot
      const hours = Math.floor(selectedTimeSlot.start / 60)
      const minutes = selectedTimeSlot.start % 60
      const bookingDateTime = setMinutes(setHours(selectedDate, hours), minutes)
      const bookingTimestamp = bookingDateTime.getTime() // Unix timestamp in milliseconds

      console.log('[BookingSelectionMessage:createBooking] Calculated booking date/time:', {
        hours,
        minutes,
        bookingDateTime: bookingDateTime.toISOString(),
        bookingTimestamp
      })

      // Prepare request JSON
      const finalEstimatedTime = estimatedTime === 'unknown' ? 1 : estimatedTime
      console.log('[BookingSelectionMessage:createBooking] Estimated time to send to backend:', finalEstimatedTime, `(originally: ${estimatedTime})`)
      
      // Build clientData from form fields, using displayName/displayText as keys
      const clientData: Record<string, string | boolean> = {}
      
      if (Array.isArray(requiredInformation)) {
        requiredInformation.forEach((fieldConfig: any, index: number) => {
          const fieldName = `field_${index}`
          const fieldKey = fieldConfig.displayText || fieldConfig.displayName || fieldName
          if (formData[fieldName] !== undefined && formData[fieldName] !== '') {
            clientData[fieldKey] = formData[fieldName]
          }
        })
      } else if (requiredInformation && typeof requiredInformation === 'object') {
        Object.entries(requiredInformation).forEach(([fieldName, fieldConfig]: any) => {
          const fieldKey = fieldConfig.displayName || fieldName
          if (formData[fieldName] !== undefined && formData[fieldName] !== '') {
            clientData[fieldKey] = formData[fieldName]
          }
        })
      }
      
      // Add auto-populated fields
      if (user?.email) clientData.email = user.email
      if (userData?.instagram) clientData.username = userData.instagram
      
      const requestData = {
        chatId: chatId,
        clientData: clientData,
        timestamp: bookingTimestamp,
        bookingDateTime: bookingDateTime.toISOString(), // Also include ISO string for readability
        artistId: artistId,
        estimatedTime: finalEstimatedTime,
        messageId: messageId // Link booking to this specific availability message
      }

      console.log('[BookingSelectionMessage:createBooking] Booking request payload:', requestData)

      // Call the create_booking endpoint
      console.log('[BookingSelectionMessage:createBooking] Sending booking creation request to backend')
      const response = await fetch(CREATE_BOOKING_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      })

      console.log('[BookingSelectionMessage:createBooking] Backend response:', {
        status: response.status,
        statusText: response.statusText
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        console.error('[BookingSelectionMessage:createBooking] ❌ Backend error:', errorData)
        throw new Error(errorData.message || `Failed to create booking: ${response.statusText}`)
      }

      const result = await response.json()
      console.log('[BookingSelectionMessage:createBooking] ✅ Booking created in backend')
      console.log('[BookingSelectionMessage:createBooking] Backend response:', result)

      if (chatId && artistId) {
        await trackEvent('chat_availability_accepted', {
          chat_id: chatId,
          artist_id: artistId,
          mode: 'booking_system',
          booking_date: bookingDateTime.toISOString().split('T')[0],
          booking_time: bookingDateTime.toISOString(),
          booking_timestamp: bookingTimestamp,
          estimated_time: estimatedTime
        })

        await sendMetaStandardEvent('Purchase', {
          currency: 'EUR',
          content_name: 'tattoo_booking',
          content_category: 'tattoo',
          num_items: 1,
        })
      }
      
      // Create iOS Calendar event if iOS Calendar is connected
      // (Google Calendar events are created by the backend)
      if (isIosCalendar && result.bookingId) {
        console.log('[BookingSelectionMessage:createBooking] 📅 Creating iOS Calendar event')
        const calendarResult = await createBookingEvent({
          clientUsername: userData?.instagram || user?.email || 'Client',
          shortSummary: `Tattoo booking with ${artistDisplayName || 'artist'}`,
          scheduledTimestamp: bookingDateTime,
          estimatedTime: finalEstimatedTime
        })
        
        if (calendarResult.eventId) {
          console.log('[BookingSelectionMessage:createBooking] ✅ iOS Calendar event created:', calendarResult.eventId)
          // Store the iOS calendar event ID in the booking document
          try {
            const bookingRef = doc(db, 'bookings', result.bookingId)
            await updateDoc(bookingRef, {
              iosCalendarEventId: calendarResult.eventId
            })
            console.log('[BookingSelectionMessage:createBooking] ✅ iosCalendarEventId saved to booking')
          } catch (updateError) {
            console.error('[BookingSelectionMessage:createBooking] Failed to save iosCalendarEventId:', updateError)
          }
        }
      }
      
      setSuccess(true)
      
      // The subscription will automatically update existingBooking state
      // Notify parent component to refetch booking (for header banner)
      if (onBookingCreated) {
        console.log('[BookingSelectionMessage:createBooking] Calling onBookingCreated callback')
        onBookingCreated()
      }
      
      console.log('[BookingSelectionMessage:createBooking] ✅ Booking creation flow complete!')
    } catch (err) {
      console.error('[BookingSelectionMessage:createBooking] ❌ Error creating booking:', err)
      console.error('[BookingSelectionMessage:createBooking] Error details:', {
        name: err instanceof Error ? err.name : 'Unknown',
        message: err instanceof Error ? err.message : String(err)
      })
      setError(err instanceof Error ? err.message : 'Failed to create booking. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Get available time slots for the selected date
  const getAvailableTimeSlots = (date: Date): TimeSlot[] => {
    if (!availability) return []

    const dateString = format(date, 'yyyy-MM-dd')
    
    // Check for date override first (highest priority) - only in week mode
    if (availability.mode === 'week') {
    const dateOverride = availability.dateOverrides?.[dateString]
    if (dateOverride) {
      let slots = dateOverride.isEnabled ? dateOverride.timeSlots : []
      
      // Subtract Google Calendar blocked slots from date override
      const googleOverride = availability.googleOverrides?.[dateString]
      if (googleOverride && googleOverride.isEnabled) {
        slots = subtractBlockedSlots(slots, googleOverride.timeSlots)
      }
      
      // Subtract existing booking blocked slots from date override
      const bookingOverride = availability.bookingOverrides?.[dateString]
      if (bookingOverride && bookingOverride.isEnabled) {
        slots = subtractBlockedSlots(slots, bookingOverride.timeSlots)
      }
      
      return slots
    }
    }

    // Determine which schedule mode to use
    const isMonthMode = availability.mode === 'month'
    
    // In month mode: ONLY use monthSchedule, no fallback to weekSchedule
    if (isMonthMode) {
      const monthScheduleEntry = availability.monthSchedule?.[dateString]
      if (!monthScheduleEntry) {
        return [] // No availability if date not in monthSchedule
      }
      
      let slots = monthScheduleEntry.isEnabled 
        ? monthScheduleEntry.timeSlots 
        : []
      
      // Subtract Google Calendar blocked slots from month schedule
      const googleOverride = availability.googleOverrides?.[dateString]
      if (googleOverride && googleOverride.isEnabled) {
        slots = subtractBlockedSlots(slots, googleOverride.timeSlots)
      }
      
      // Subtract existing booking blocked slots from month schedule
      const bookingOverride = availability.bookingOverrides?.[dateString]
      if (bookingOverride && bookingOverride.isEnabled) {
        slots = subtractBlockedSlots(slots, bookingOverride.timeSlots)
      }
      
      return slots
    }

    // In week mode: use weekSchedule (or weeklySchedule for backward compatibility)
    const dayName = getDayName(date)
    const weekSchedule = availability.weekSchedule || (availability as any).weeklySchedule
    
    if (!weekSchedule) return []
    
    const daySettings = weekSchedule[dayName]
    
    if (!daySettings?.isEnabled) return []
    
    // Subtract Google Calendar blocked slots from weekly schedule
    let availableSlots = daySettings.timeSlots
    const googleOverride = availability.googleOverrides?.[dateString]
    if (googleOverride && googleOverride.isEnabled) {
      availableSlots = subtractBlockedSlots(availableSlots, googleOverride.timeSlots)
    }
    
    // Subtract existing booking blocked slots from weekly schedule
    const bookingOverride = availability.bookingOverrides?.[dateString]
    if (bookingOverride && bookingOverride.isEnabled) {
      availableSlots = subtractBlockedSlots(availableSlots, bookingOverride.timeSlots)
    }
    
    return availableSlots
  }

  // Break time slots into 30-minute intervals
  const get30MinuteIntervals = (timeSlots: TimeSlot[]): { start: number; end: number }[] => {
    const intervals: { start: number; end: number }[] = []
    
    // Convert estimated time to minutes (handle 'unknown' case - default to 1 hour)
    const durationMinutes = estimatedTime === 'unknown' ? 60 : estimatedTime * 60
    
    timeSlots.forEach(slot => {
      let currentStart = slot.start
      while (currentStart + 30 <= slot.end) {
        // Check if there's enough time to complete the tattoo after this slot starts
        const endTime = currentStart + 30
        const remainingTime = slot.end - currentStart
        
        // Only add the slot if there's enough time for the estimated duration
        // If duration is unknown, include all slots
        if (durationMinutes === 0 || remainingTime >= durationMinutes) {
        intervals.push({
          start: currentStart,
            end: endTime
        })
        }
        currentStart += 30
      }
    })
    
    return intervals
  }

  // Check if a date has any available time slots
  const hasAvailableSlots = (date: Date): boolean => {
    const slots = getAvailableTimeSlots(date)
    return get30MinuteIntervals(slots).length > 0
  }

  const monthStart = startOfMonth(currentMonth)
  const monthEnd = endOfMonth(currentMonth)
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd })

  // Get day names for the header (Mon-Sun)
  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

  // Calculate padding days for the start of the month
  const startPadding = monthStart.getDay() === 0 ? 6 : monthStart.getDay() - 1
  const paddingDays = Array.from({ length: startPadding }, () => null)

  // Animation variants
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction * 50,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction * -50,
      opacity: 0
    })
  }

  const rawSlots = selectedDate ? getAvailableTimeSlots(selectedDate) : []
  const availableSlots = get30MinuteIntervals(rawSlots)

  // Function to check if form is valid
  const isFormValid = () => {
    if (!selectedDate || !selectedTimeSlot) return false

    // Check if all required fields are filled
    if (Array.isArray(requiredInformation)) {
      for (let i = 0; i < requiredInformation.length; i++) {
        const fieldConfig = requiredInformation[i]
        const fieldName = `field_${i}`
        if (fieldConfig?.type === 'consentForm' && isNative()) continue
        if (fieldConfig && fieldConfig.isRequired && (formData[fieldName] === '' || formData[fieldName] === false)) {
          return false
        }
      }
    } else if (requiredInformation && typeof requiredInformation === 'object') {
      for (const fieldName in formData) {
        const fieldConfig = requiredInformation?.[fieldName]
        if (fieldConfig?.type === 'consentForm' && isNative()) continue
        if (fieldConfig && fieldConfig.isRequired && (formData[fieldName] === '' || formData[fieldName] === false)) {
          return false
        }
      }
    }
    return true
  }

  // Monitor if the selected time slot becomes unavailable while on form step
  useEffect(() => {
    if (bookingStep === 'form' && selectedDate && selectedTimeSlot && availability) {
      // Get current available slots for the selected date
      const currentSlots = getAvailableTimeSlots(selectedDate)
      const currentIntervals = get30MinuteIntervals(currentSlots)
      
      // Check if the selected time slot still exists in the available slots
      const isStillAvailable = currentIntervals.some(
        slot => slot.start === selectedTimeSlot.start && slot.end === selectedTimeSlot.end
      )
      
      // If the slot is no longer available, show error and go back to calendar
      if (!isStillAvailable) {
        console.log('[BookingSelectionMessage] ⚠️ Selected time slot is no longer available, returning to calendar')
        setError('The time slot you selected is no longer available. Please select a different time.')
        setBookingStep('calendar')
        setSelectedTimeSlot(null)
      }
    }
  }, [availability, bookingStep, selectedDate, selectedTimeSlot])

  return (
    <>
      <div className="w-full mb-4 flex justify-center relative">
        <motion.div 
          id={messageId ? `booking-selection-message-${messageId}` : undefined}
          ref={messageRef}
          {...(isOwnMessage ? longPressHandlers : {})}
          className={cn(
            "rounded-[18px] p-4 shadow-sm w-full max-w-[min(100%,520px)]",
            isTime4TatsBooking ? "time4tats-btn" : "bg-primary text-white"
          )}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ 
            opacity: showDeleteOverlay ? 0 : 1, 
            scale: 1,
            y: 0
          }}
          style={{
            position: 'static',
            zIndex: 'auto'
          }}
          transition={{ type: "spring", damping: 20, stiffness: 300 }}
        >
        {/* Booking Confirmed View - shown when booking exists */}
        {existingBooking ? (
          <>
            {/* Completed Booking View */}
            {existingBooking.status === 'completed' ? (
              <>
                {/* Success Icon and Message */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center bg-black/30">
                    <Check className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-base text-white">
                      Tattoo Done!
                    </h4>
                    <p className="text-sm text-white/80">
                      {isTime4TatsBooking
                        ? 'time4tats, May 9, 2026'
                        : format(existingBooking.scheduledTimestamp.toDate(), 'EEEE, MMMM d, yyyy')}
                    </p>
                  </div>
                </div>

                {/* Booking Details */}
                <div className="space-y-2 text-sm border-t pt-3 border-white/20 text-white/90">
                  <div className="flex justify-between items-center gap-2">
                    <span className="text-white/70 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-white/70" />
                      Scheduled Time:
                    </span>
                    <span className="font-medium">
                      {format(existingBooking.scheduledTimestamp.toDate(), 'HH:mm')}
                    </span>
                  </div>
                  <div className="flex justify-between items-center gap-2">
                    <span className="text-white/70 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-white/70" />
                      Estimated Duration:
                    </span>
                    <span className="font-medium">{estimatedTime === 'unknown' ? 'Unknown' : `${estimatedTime}h`}</span>
                  </div>
                  {existingBooking.bookingCompleteForm?.finalPrice && (
                    <div className="flex justify-between items-center gap-2">
                      <span className="text-white/70">Final Price:</span>
                      <span className="font-medium">€{existingBooking.bookingCompleteForm.finalPrice}</span>
                    </div>
                  )}
                </div>
              </>
            ) : existingBooking.status === 'no_show' ? (
              <>
                {/* No Show Icon and Message */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center bg-red-500/20">
                    <X className="w-6 h-6 text-red-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-base text-white">
                      Booking - No Show
                    </h4>
                    <p className="text-sm text-white/80">
                      {format(existingBooking.scheduledTimestamp.toDate(), 'EEEE, MMMM d, yyyy')}
                    </p>
                  </div>
                </div>

                {/* No Show Message */}
                <div className="mb-4 p-3 rounded-md bg-white/10">
                  <p className="text-xs text-white/80">
                    {isArtistView 
                      ? "This booking was marked as no-show."
                      : "This booking was missed. Please reach out to reschedule."}
                  </p>
                </div>

                {/* Booking Details */}
                <div className="space-y-2 text-sm border-t pt-3 border-white/20 text-white/90">
                  <div className="flex justify-between items-center gap-2">
                    <span className="text-white/70 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-white/70" />
                      Scheduled Time:
                    </span>
                    <span className="font-medium">
                      {format(existingBooking.scheduledTimestamp.toDate(), 'HH:mm')}
                    </span>
                  </div>
                  <div className="flex justify-between items-center gap-2">
                    <span className="text-white/70 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-white/70" />
                      Estimated Duration:
                    </span>
                    <span className="font-medium">{estimatedTime === 'unknown' ? 'Unknown' : `${estimatedTime}h`}</span>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Active Booking View (scheduled/confirmed) */}
                {/* Success Icon and Message */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center bg-black/30">
                    <Check className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-base text-white">
                      Booking Confirmed!
                    </h4>
                    <p className="text-sm text-white/80">
                      {isTime4TatsBooking
                        ? 'time4tats, May 9, 2026'
                        : format(existingBooking.scheduledTimestamp.toDate(), 'EEEE, MMMM d, yyyy')}
                    </p>
                  </div>
                </div>

                {/* Booking Details */}
                <div className="space-y-2 text-sm border-t pt-3 border-white/20 text-white/90">
                  <div className="flex justify-between">
                    <span className="text-white/70">Time:</span>
                    <span className="font-medium">{format(existingBooking.scheduledTimestamp.toDate(), 'h:mm a')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/70">Duration:</span>
                    <span className="font-medium">{estimatedTime === 'unknown' ? 'Unknown' : `${estimatedTime}h`}</span>
                  </div>
                </div>

                {/* Cancel Button - only show for artists */}
                {isArtistView && (
                  <motion.button
                    onClick={handleCancelBooking}
                    disabled={isCancelling}
                    whileHover={{ scale: isCancelling ? 1 : 1.02 }}
                    whileTap={{ scale: isCancelling ? 1 : 0.98 }}
                    className="mt-4 w-full py-2.5 px-4 rounded-md text-sm font-medium bg-black/30 hover:bg-black/40 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    type="button"
                  >
                    {isCancelling ? (
                      <>
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Cancelling...
                      </>
                    ) : (
                      <>
                        <X className="w-4 h-4" />
                        Cancel Booking
                      </>
                    )}
                  </motion.button>
                )}
              </>
            )}
          </>
        ) : (
          <>
            {/* Header info - only show when no booking exists */}
              <div className="mb-4">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="text-base font-semibold mb-3 text-white">
                    {isArtistView ? 'Available Times' : `Book your time with ${artistDisplayName || senderName || 'artist'} `}
                  </h3>
                  {isArtistView && (
                    <button
                      onClick={() => navigate('/booking-system/availability')}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-white/15 hover:bg-white/25 text-white transition-colors"
                      type="button"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                      Edit
                    </button>
                  )}
                </div>
                <div className="space-y-2 text-sm text-white">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-white/70" />
                    <span className="text-white/70">
                      Estimated duration:{' '}
                    </span>
                    <span className="font-medium">
                      {isArtistView 
                        ? (estimatedTime === 'unknown' ? 'Not set' : `${estimatedTime}h`)
                        : (estimatedTime === 'unknown' ? 'Unknown' : `${estimatedTime}h`)
                      }
                    </span>
                  </div>
                  {isArtistView && (
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-white/70" />
                      <span className="text-white/70">
                        {preparationDays !== undefined 
                          ? `Preparation time: ${preparationDays} day${preparationDays !== 1 ? 's' : ''}`
                          : `First available: ${format(earliestDate, 'MMM d, yyyy')}`}
                      </span>
                    </div>
                  )}
                </div>
              </div>
          </>
        )}

        {/* Artist View: Display calendar and times */}
        {isArtistView && !existingBooking && (
          <div>
            <div className="text-center mb-3">
              <p className="text-xs text-white/90">
                Waiting for the client to book a time
              </p>
            </div>
            
          <div className="grid grid-cols-1 gap-4 md:px-[20%]">
            {/* Left: Calendar - show when no date selected or during highlight animation */}
            {(!selectedDate || justSelectedDate) && (
              <motion.div 
                className="bg-card rounded-lg p-3 w-full"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
              >
            {/* Calendar Header */}
            <div className="flex items-center justify-between mb-3">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={handlePrevMonth}
                className="p-1 rounded-full hover:bg-muted"
                type="button"
              >
                <ChevronLeft className="w-4 h-4 text-foreground" />
              </motion.button>
              
              <motion.h2 
                key={currentMonth.toISOString()}
                custom={slideDirection}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                className="text-sm font-medium text-foreground"
              >
                {format(currentMonth, 'MMMM yyyy')}
              </motion.h2>
              
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleNextMonth}
                className="p-1 rounded-full hover:bg-muted"
                type="button"
              >
                <ChevronRight className="w-4 h-4 text-foreground" />
              </motion.button>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1 aspect-square">
              {/* Day names */}
              {dayNames.map(day => (
                <div key={day} className="text-center text-xs font-medium text-muted-foreground py-1">
                  {day}
                </div>
              ))}
              
              {/* Padding days */}
              {paddingDays.map((_, i) => (
                <div key={`pad-${i}`} className="aspect-square" />
              ))}
              
              {/* Actual days */}
              {daysInMonth.map(date => {
                const isSelected = selectedDate && isSameDay(date, selectedDate)
                const isPast = isBefore(date, startOfToday())
                const isBeforeEarliest = isBefore(date, earliestDate)
                const hasSlots = hasAvailableSlots(date)
                const isDisabled = isPast || isBeforeEarliest || !hasSlots
                
                return (
                  <motion.button
                    key={date.toISOString()}
                    whileHover={{ scale: isDisabled ? 1 : 0.95 }}
                    whileTap={{ scale: isDisabled ? 1 : 0.9 }}
                    onClick={() => !isDisabled && handleDayClick(date)}
                    disabled={isDisabled}
                    type="button"
                    className={cn(
                      "aspect-square rounded-md flex items-center justify-center text-xs transition-colors relative",
                      isSelected && "bg-primary text-primary-foreground",
                      !isSelected && !isDisabled && "hover:bg-muted",
                      isToday(date) && !isSelected && !isDisabled && "border border-primary",
                      !isSameMonth(date, currentMonth) && "text-muted-foreground opacity-50",
                      isDisabled && "opacity-30 cursor-not-allowed",
                      !isSelected && "text-foreground"
                    )}
                  >
                    {format(date, 'd')}
                  </motion.button>
                )
              })}
            </div>
              </motion.div>
            )}

            {/* Right: Time Slots - only show when date selected and highlight is done */}
            {selectedDate && !justSelectedDate && (
              <motion.div 
                className="bg-card rounded-lg p-3 min-h-[280px]"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
              >
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-foreground">
                    {`Available Times - ${format(selectedDate, 'MMM d')}`}
                  </h4>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleDayClick(selectedDate)}
                    className="p-1 rounded-full hover:bg-muted text-muted-foreground"
                    type="button"
                    title="Back to calendar"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </motion.button>
            </div>

                {availableSlots.length > 0 ? (
                  <div className="space-y-2 max-h-[200px] overflow-y-auto mb-3">
                    {availableSlots.map((slot, index) => (
                      <div
                        key={index}
                        className="w-full flex items-center justify-between px-3 py-2 rounded-md text-sm bg-muted text-foreground cursor-not-allowed opacity-60"
                      >
                        <span className="font-medium">
                          {formatTime(slot.start)}
                        </span>
                        <Clock className="w-4 h-4" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                    No available times on this date
                  </div>
                )}
              </motion.div>
            )}
          </div>
        </div>
          )}

        {/* User View: Calendar or Time Slots (animated transition) */}
        {!isArtistView && !existingBooking && (
          <div>
            {/* Container with fixed height */}
            <div className="grid grid-cols-1 gap-4">
              {/* Step 1: Calendar and Time Selection */}
              {bookingStep === 'calendar' && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  className="grid grid-cols-1 md:grid-cols-2 gap-4"
                >
            {/* Left: Calendar - show when no date selected or during highlight animation */}
            {(!selectedDate || justSelectedDate) && (
              <motion.div 
                className="bg-card rounded-lg p-3 w-full"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
              >
            {/* Calendar Header */}
            <div className="flex items-center justify-between mb-2">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={handlePrevMonth}
                className="p-1 rounded-full hover:bg-muted"
                type="button"
              >
                <ChevronLeft className="w-4 h-4 text-foreground" />
              </motion.button>
              
              <motion.h2 
                key={currentMonth.toISOString()}
                custom={slideDirection}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                className="text-sm font-medium text-foreground"
              >
                {format(currentMonth, 'MMMM yyyy')}
              </motion.h2>
              
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleNextMonth}
                className="p-1 rounded-full hover:bg-muted"
                type="button"
              >
                <ChevronRight className="w-4 h-4 text-foreground" />
              </motion.button>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1 aspect-square">
              {/* Day names */}
              {dayNames.map(day => (
                <div key={day} className="text-center text-xs font-medium text-muted-foreground py-0.5">
                  {day}
                </div>
              ))}
              
              {/* Padding days */}
              {paddingDays.map((_, i) => (
                <div key={`pad-${i}`} className="aspect-square" />
              ))}
              
              {/* Actual days */}
              {daysInMonth.map(date => {
                const isSelected = selectedDate && isSameDay(date, selectedDate)
                const isPast = isBefore(date, startOfToday())
                const isBeforeEarliest = isBefore(date, earliestDate)
                const hasSlots = hasAvailableSlots(date)
                const isDisabled = isPast || isBeforeEarliest || !hasSlots
                
                return (
                  <motion.button
                    key={date.toISOString()}
                    whileHover={{ scale: isDisabled ? 1 : 0.95 }}
                    whileTap={{ scale: isDisabled ? 1 : 0.9 }}
                    onClick={() => !isDisabled && handleDayClick(date)}
                    disabled={isDisabled}
                    type="button"
                    className={cn(
                      "aspect-square rounded-md flex items-center justify-center text-xs transition-colors relative",
                      isSelected && "bg-primary text-primary-foreground",
                      !isSelected && !isDisabled && "hover:bg-muted",
                      isToday(date) && !isSelected && !isDisabled && "border border-primary",
                      !isSameMonth(date, currentMonth) && "text-muted-foreground opacity-50",
                      isDisabled && "opacity-30 cursor-not-allowed",
                      !isSelected && "text-foreground"
                    )}
                  >
                    {format(date, 'd')}
                  </motion.button>
                )
              })}
            </div>
              </motion.div>
            )}

            {/* Right: Time Slots - only show when date selected and highlight is done */}
            {selectedDate && !justSelectedDate && (
              <motion.div 
                className="bg-card rounded-lg p-3 min-h-[280px]"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
              >
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-foreground">
                    {`Available Times - ${format(selectedDate, 'MMM d')}`}
                  </h4>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleDayClick(selectedDate)}
                    className="p-1 rounded-full hover:bg-muted text-muted-foreground"
                    type="button"
                    title="Back to calendar"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </motion.button>
          </div>

                {availableSlots.length > 0 ? (
                <>
                        <div className="space-y-2 max-h-[200px] overflow-y-auto mb-3">
                    {availableSlots.map((slot, index) => {
                      const isSelected = selectedTimeSlot?.start === slot.start && selectedTimeSlot?.end === slot.end
                      return (
                        <motion.button
                          key={index}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                          onClick={() => handleTimeSlotClick(slot)}
                          className={cn(
                            "w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors",
                            isSelected 
                              ? "bg-primary text-primary-foreground" 
                              : "bg-muted hover:bg-muted/80 text-foreground"
                          )}
                          type="button"
                        >
                          <span className="font-medium">
                            {formatTime(slot.start)}
                          </span>
                          <Clock className="w-4 h-4" />
                        </motion.button>
                      )
                    })}
                  </div>
                  {selectedTimeSlot && (
                    <motion.button
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      onClick={handleNextClick}
                      className="w-full bg-primary text-primary-foreground py-2 px-4 rounded-md text-sm font-medium hover:bg-primary/90 transition-colors"
                      type="button"
                    >
                      Next
                    </motion.button>
                  )}
                </>
              ) : (
                <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                  No available times on this date
                </div>
                )}
              </motion.div>
            )}
            </motion.div>
              )}

              {/* Step 2: Booking Form */}
              {bookingStep === 'form' && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  className="bg-card rounded-lg p-4"
                >
                  <h4 className="text-sm font-medium text-foreground mb-4">Complete Your Booking</h4>
                  
                  <div className="space-y-3 mb-4">
                    {/* Dynamic Form Fields */}
                    {requiredInformation && Array.isArray(requiredInformation) ? (
                      requiredInformation.map((fieldConfig: any, index: number) => {
                        const fieldName = `field_${index}`
                        if (fieldConfig.type === 'input' || fieldConfig.type === 'text') {
                          const value = (formData[fieldName] as string) || ''
                          return (
                            <div key={fieldName}>
                              <label className="block text-xs font-medium text-foreground mb-1">
                                {fieldConfig.displayText || fieldConfig.displayName || fieldName}
                                {fieldConfig.isRequired && ' *'}
                              </label>
                              <input
                                type="text"
                                value={value}
                                onChange={(e) => setFormData(prev => ({ ...prev, [fieldName]: e.target.value }))}
                                className="w-full px-3 py-2 text-base text-foreground border border-border rounded-md focus:outline-none focus:ring-0 bg-background"
                                placeholder={fieldConfig.placeholder || fieldConfig.hint || fieldConfig.displayText || fieldConfig.displayName || fieldName}
                                required={fieldConfig.isRequired}
                              />
                            </div>
                          )
                        } else if (fieldConfig.type === 'consentForm') {
                          if (isNative()) return null
                          const isChecked = (formData[fieldName] as boolean) || false
                          const displayLabel = fieldConfig.displayText || fieldConfig.displayName || 'Consent Form'
                          return (
                            <div key={fieldName} className="flex items-start gap-3">
                              <input
                                type="checkbox"
                                id={fieldName}
                                checked={isChecked}
                                onChange={(e) => setFormData(prev => ({ ...prev, [fieldName]: e.target.checked }))}
                                className="mt-1 h-4 w-4 bg-primary text-primary border-primary rounded focus:ring-0 cursor-pointer flex-shrink-0 accent-primary"
                                required={fieldConfig.isRequired}
                              />
                              <div className="flex-1 flex items-center justify-between">
                                <label htmlFor={fieldName} className="text-xs text-muted-foreground cursor-pointer">
                                  I have checked the{' '}
                                  <span className="font-medium text-foreground">
                                    {displayLabel}
                                  </span>
                                  {' '}and agree
                                </label>
                                <motion.button
                                  type="button"
                                  onClick={() => setActiveConsentField(fieldName)}
                                  className="ml-2 px-3 py-1 text-xs font-medium bg-muted hover:bg-muted/80 text-foreground rounded-md transition-colors flex-shrink-0"
                                  whileHover={{ scale: 1.02 }}
                                  whileTap={{ scale: 0.98 }}
                                >
                                  View
                                </motion.button>
                              </div>
                            </div>
                          )
                        }
                        return null
                      })
                    ) : requiredInformation && typeof requiredInformation === 'object' && !Array.isArray(requiredInformation) ? (
                      Object.entries(requiredInformation).map(([fieldName, fieldConfig]: any) => {
                        if (fieldConfig.type === 'input' || fieldConfig.type === 'text') {
                          const value = (formData[fieldName] as string) || ''
                          return (
                            <div key={fieldName}>
                              <label className="block text-xs font-medium text-foreground mb-1">
                                {fieldConfig.displayName || fieldName}
                                {fieldConfig.isRequired && ' *'}
                              </label>
                              <input
                                type="text"
                                value={value}
                                onChange={(e) => setFormData(prev => ({ ...prev, [fieldName]: e.target.value }))}
                                className="w-full px-3 py-2 text-base text-foreground border border-border rounded-md focus:outline-none focus:ring-0 bg-background"
                                placeholder={fieldConfig.placeholder || fieldConfig.hint || fieldConfig.displayName || fieldName}
                                required={fieldConfig.isRequired}
                              />
                            </div>
                          )
                        } else if (fieldConfig.type === 'consentForm') {
                          if (isNative()) return null
                          const isChecked = (formData[fieldName] as boolean) || false
                          const displayLabel = fieldConfig.displayText || fieldConfig.displayName || 'Consent Form'
                          return (
                            <div key={fieldName} className="flex items-start gap-3">
                              <input
                                type="checkbox"
                                id={fieldName}
                                checked={isChecked}
                                onChange={(e) => setFormData(prev => ({ ...prev, [fieldName]: e.target.checked }))}
                                className="mt-1 h-4 w-4 bg-primary text-primary border-primary rounded focus:ring-0 cursor-pointer flex-shrink-0 accent-primary"
                                required={fieldConfig.isRequired}
                              />
                              <div className="flex-1 flex items-center justify-between">
                                <label htmlFor={fieldName} className="text-xs text-muted-foreground cursor-pointer">
                                  I have checked the{' '}
                                  <span className="font-medium text-foreground">
                                    {displayLabel}
                                  </span>
                                  {' '}and agree
                                </label>
                                <motion.button
                                  type="button"
                                  onClick={() => setActiveConsentField(fieldName)}
                                  className="ml-2 px-3 py-1 text-xs font-medium bg-muted hover:bg-muted/80 text-foreground rounded-md transition-colors flex-shrink-0"
                                  whileHover={{ scale: 1.02 }}
                                  whileTap={{ scale: 0.98 }}
                                >
                                  View
                                </motion.button>
                              </div>
                            </div>
                          )
                        }
                        return null
                      })
                    ) : null}

                    {/* Fallback: Show message if no fields configured */}
                    {!requiredInformation || (Array.isArray(requiredInformation) && requiredInformation.length === 0) || (typeof requiredInformation === 'object' && !Array.isArray(requiredInformation) && Object.keys(requiredInformation).length === 0) && (
                      <p className="text-xs text-muted-foreground py-2">No additional information required.</p>
                    )}
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="mb-3 p-3 bg-destructive/10 border border-destructive/50 rounded-md">
                      <p className="text-xs text-destructive">{error}</p>
                    </div>
                  )}

                  {/* Success Message */}
                  {success && (
                    <div className="mb-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
                      <p className="text-xs text-green-600 dark:text-green-400">Booking created successfully! The artist will confirm shortly.</p>
                    </div>
                  )}

                  {/* Booking Summary & Action */}
                  <div className="flex items-center justify-between gap-3 pt-3 border-t border-border">
                    <div className="text-xs text-muted-foreground">
                      <div className="font-medium text-foreground">{format(selectedDate!, 'MMM d, yyyy')}</div>
                      <div>{formatTime(selectedTimeSlot!.start)} ({estimatedTime === 'unknown' ? 'Unknown' : `${estimatedTime}h`})</div>
                    </div>
                    <div className="flex gap-2">
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={handleBackToCalendar}
                        disabled={isSubmitting || success}
                        className={cn(
                          "px-4 py-2 text-sm font-medium rounded-md transition-colors",
                          isSubmitting || success
                            ? "bg-muted text-muted-foreground cursor-not-allowed"
                            : "text-foreground bg-muted hover:bg-muted/80"
                        )}
                        type="button"
                      >
                        Back
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: isSubmitting || success ? 1 : 1.02 }}
                        whileTap={{ scale: isSubmitting || success ? 1 : 0.98 }}
                        onClick={handleBookTime}
                        disabled={!isFormValid() || isSubmitting || success}
                        className={cn(
                          "px-4 py-2 text-sm font-medium text-primary-foreground rounded-md transition-colors min-w-[100px]",
                          isFormValid() && !isSubmitting && !success
                            ? "bg-primary hover:bg-primary/90"
                            : "bg-muted text-muted-foreground cursor-not-allowed"
                        )}
                        type="button"
                      >
                        {isSubmitting ? (
                          <span className="flex items-center gap-2">
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Booking...
                          </span>
                        ) : success ? (
                          'Booked!'
                        ) : (
                          'Book Time'
                        )}
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Step Indicator - Moved to Bottom */}
            <div className="flex items-center justify-center gap-2 mt-4">
              <div className={cn(
                "w-2 h-2 rounded-full transition-all",
                bookingStep === 'calendar' ? "bg-white" : "border border-white"
              )} />
              <div className={cn(
                "w-2 h-2 rounded-full transition-all",
                bookingStep === 'form' ? "bg-white" : "border border-white"
              )} />
            </div>
          </div>
        )}
      </motion.div>

        {/* Consent Modal */}
        {activeConsentField && requiredInformation && (
          <ConsentModal
            isOpen={!!activeConsentField}
            title={
              Array.isArray(requiredInformation)
                ? requiredInformation[parseInt(activeConsentField.split('_')[1])]?.displayText ||
                  requiredInformation[parseInt(activeConsentField.split('_')[1])]?.displayName ||
                  'Consent Form'
                : requiredInformation[activeConsentField]?.displayName || 'Terms & Conditions'
            }
            content={
              Array.isArray(requiredInformation)
                ? requiredInformation[parseInt(activeConsentField.split('_')[1])]?.content || ''
                : requiredInformation[activeConsentField]?.content || ''
            }
            onClose={() => setActiveConsentField(null)}
          />
        )}
      </div>

      {/* Instagram-style Blur Overlay with Delete Option - Using Portal */}
      {showDeleteOverlay && createPortal(
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50"
            style={{ 
              left: 0,
              right: 0,
              top: 0,
              bottom: 0,
              margin: 0
            }}
          >
            {/* Blur backdrop - covers entire viewport including header and input */}
            <div
              onClick={handleCancelDelete}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            />
            
            {/* Clone of the message above the blur - unblurred */}
            {messageSnapshot && (
              <motion.div
                initial={{ scale: 1 }}
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ 
                  duration: 0.3,
                  times: [0, 0.5, 1],
                  ease: "easeInOut"
                }}
                className={messageSnapshot.className}
                style={{
                  position: 'absolute',
                  top: messagePosition.top,
                  left: messagePosition.left,
                  width: messagePosition.width,
                  height: messagePosition.height,
                  zIndex: 60,
                  pointerEvents: 'none'
                }}
                dangerouslySetInnerHTML={{ __html: messageSnapshot.html }}
              />
            )}
            
            {/* Unsend button positioned below the message */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ type: "spring", damping: 20, stiffness: 300 }}
              className="absolute flex gap-3"
              style={{
                top: `${messagePosition.top + messagePosition.height + 16}px`,
                right: `${window.innerWidth - (messagePosition.left + messagePosition.width)}px`,
              }}
            >
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className={cn(
                  "flex items-center gap-2 px-6 py-3 rounded-[18px]",
                  "bg-background text-red-500 font-semibold text-sm",
                  "hover:bg-muted active:scale-95",
                  "transition-all shadow-lg",
                  "disabled:opacity-50 disabled:cursor-not-allowed"
                )}
              >
                <Undo2 className="w-5 h-5" />
                <span>{isDeleting ? 'Unsending...' : 'Unsend'}</span>
              </button>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}
    </>
  )
}
